import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Tooltip,
    Chip,
    Snackbar,
    Alert,
    TextField,
    InputAdornment,
    Fab,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
    Edit as EditIcon,
    Add as AddIcon,
    Block as BlockIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    Assignment as AssignmentIcon,
} from '@mui/icons-material';

import TeamForm from './TeamForm';
import { useTeams } from '../../../hooks/taxi/useTeams';
import { useAuthContext } from '../../../context/AuthProvider';

const ADMIN_ID = Number(process.env.REACT_APP_ROL_ADMIN_ID);

const TeamsDashboard = () => {
    // Estados UI
    const [open, setOpen] = useState(false);
    const [editTeam, setEditTeam] = useState(null);
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL'); // ALL | A | I

    // Estado para confirmación de eliminación
    const [openConfirm, setOpenConfirm] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);

    const { user } = useAuthContext();
    // Asegurar que teams es un array (si el hook devuelve el objeto completo, extraemos .data)
    const { teams: teamsData = [], isLoading, error, createMutation, updateServicesMutation, deleteMutation } = useTeams();
    // Si el hook ya devuelve el array directamente, usar teamsData. Si devuelve el objeto con data, extraer:
    const teams = teamsData?.data ?? teamsData;

    // --- Callbacks ---
    const showAlert = useCallback((message, severity = 'success') => {
        setAlert({ open: true, message, severity });
    }, []);

    const handleOpenCreate = useCallback(() => {
        setEditTeam(null);
        setOpen(true);
    }, []);

    const handleOpenEdit = useCallback((team) => {
        setEditTeam(team);
        setOpen(true);
    }, []);

    const handleCloseForm = useCallback(() => {
        setOpen(false);
        setEditTeam(null);
    }, []);

    const handleOpenConfirm = useCallback((team) => {
        setSelectedTeam(team);
        setOpenConfirm(true);
    }, []);

    const handleCloseConfirm = useCallback(() => {
        setOpenConfirm(false);
        setSelectedTeam(null);
    }, []);

    // --- Mutaciones ---
    const handleSubmitTeam = useCallback(
        (data) => {
            if (!user) {
                showAlert('Debe iniciar sesión para realizar esta acción', 'error');
                return;
            }

            if (editTeam) {
                updateServicesMutation.mutate(
                    {
                        equConVehId: editTeam.equConVehId,
                        data: { serProIds: data.serProIds },
                    },
                    {
                        onSuccess: () => {
                            showAlert('Servicios del equipo actualizados con éxito');
                            handleCloseForm();
                        },
                        onError: (err) => showAlert(err?.message || 'Error al actualizar el equipo', 'error'),
                    }
                );
            } else {
                createMutation.mutate(
                    {
                        empId: data.empId,
                        vehId: data.vehId,
                        nombre: data.nombre,
                        serProIds: data.serProIds,
                    },
                    {
                        onSuccess: () => {
                            showAlert('Equipo creado con éxito');
                            handleCloseForm();
                        },
                        onError: (err) => showAlert(err?.message || 'Error al crear el equipo', 'error'),
                    }
                );
            }
        },
        [user, editTeam, createMutation, updateServicesMutation, showAlert, handleCloseForm]
    );

    const handleConfirmDelete = useCallback(() => {
        if (selectedTeam) {
            deleteMutation.mutate(selectedTeam.equConVehId, {
                onSuccess: () => {
                    showAlert('Equipo eliminado con éxito');
                    handleCloseConfirm();
                },
                onError: (err) => {
                    showAlert(err?.message || 'Error al eliminar el equipo', 'error');
                    handleCloseConfirm();
                },
            });
        }
    }, [selectedTeam, deleteMutation, showAlert, handleCloseConfirm]);

    // --- Memoización de Datos ---
    const normalizedSearch = search.trim().toLowerCase();

    const rows = useMemo(() => {
        let data = Array.isArray(teams) ? teams : [];

        // Filtro por estado
        if (statusFilter === 'A') {
            data = data.filter((t) => t.estReg === 'A');
        } else if (statusFilter === 'I') {
            data = data.filter((t) => t.estReg !== 'A');
        }

        // Búsqueda inteligente (nombre del equipo, conductor, vehículo)
        if (normalizedSearch) {
            data = data.filter((t) => {
                const name = (t.equConVehNom || '').toLowerCase();
                const driver = `${t.perNom || ''} ${t.perApe || ''}`.toLowerCase();
                const plate = (t.vehMat || '').toLowerCase();
                return (
                    name.includes(normalizedSearch) ||
                    driver.includes(normalizedSearch) ||
                    plate.includes(normalizedSearch)
                );
            });
        }

        return data;
    }, [teams, statusFilter, normalizedSearch]);

    // --- Columnas ---
    const columns = useMemo(
        () => [
            {
                field: 'equConVehNom',
                headerName: 'Nombre del equipo',
                width: 180,
                valueGetter: (params) => params.row?.equConVehNom || 'Sin nombre',
            },
            {
                field: 'driver',
                headerName: 'Conductor',
                width: 200,
                valueGetter: (params) => {
                    const row = params.row;
                    return `${row.perNom || ''} ${row.perApe || ''}`.trim() || 'Sin conductor';
                },
            },
            {
                field: 'vehMat',
                headerName: 'Vehículo',
                width: 140,
                valueGetter: (params) => params.row?.vehMat || 'Sin vehículo',
            },
            {
                field: 'servicios',
                headerName: 'Servicios',
                flex: 1,
                minWidth: 200,
                renderCell: (params) => {
                    const services = params.row?.serviciosProyecto || [];
                    if (services.length === 0) return <Chip label="Sin servicios" size="small" variant="outlined" />;
                    return (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {services.slice(0, 3).map((s) => (
                                <Chip key={s.serProId} label={s.serProNom || 'Servicio'} size="small" />
                            ))}
                            {services.length > 3 && <Chip label={`+${services.length - 3}`} size="small" />}
                        </Stack>
                    );
                },
            },
            {
                field: 'estReg',
                headerName: 'Estado',
                width: 120,
                sortable: true,
                renderCell: (params) => {
                    const active = params.value === 'A';
                    return <Chip label={active ? 'ACTIVO' : 'INACTIVO'} color={active ? 'success' : 'warning'} size="small" variant="outlined" />;
                },
            },
            {
                field: 'actions',
                headerName: 'Acciones',
                width: 200,
                sortable: false,
                filterable: false,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => {
                    const team = params.row;
                    const canDelete = team.estReg !== 'E' && user?.RolID === ADMIN_ID;

                    return (
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Editar servicios">
                                <IconButton size="small" onClick={() => handleOpenEdit(team)}>
                                    <EditIcon color="primary" fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Ver detalles">
                                <IconButton size="small" color="info">
                                    <AssignmentIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            {canDelete && (
                                <Tooltip title="Eliminar">
                                    <IconButton size="small" onClick={() => handleOpenConfirm(team)} disabled={deleteMutation.isPending}>
                                        <BlockIcon color="error" fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                    );
                },
            },
        ],
        [user?.RolID, handleOpenEdit, handleOpenConfirm, deleteMutation.isPending]
    );

    return (
        <Paper
            sx={{
                p: 2.5,
                borderRadius: 2,
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#f9f9f9',
            }}
        >
            <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                {/* Cabecera y Filtros */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" fontWeight={700}>
                        Equipos (Conductor + Vehículo)
                    </Typography>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={statusFilter}
                        onChange={(_, val) => val && setStatusFilter(val)}
                    >
                        <ToggleButton value="ALL">Todos</ToggleButton>
                        <ToggleButton value="A">Activos</ToggleButton>
                        <ToggleButton value="I">Inactivos</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <TextField
                    size="small"
                    label="Buscar por nombre, conductor o vehículo..."
                    variant="outlined"
                    fullWidth
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <Tooltip title="Limpiar">
                                    <IconButton size="small" onClick={() => setSearch('')}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ) : null,
                    }}
                />

                {error && <Alert severity="error">{error.message || 'Error al cargar los equipos'}</Alert>}

                {/* DataGrid */}
                <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={isLoading}
                        getRowId={(row) => row.equConVehId} // ← CORREGIDO: mayúsculas
                        disableRowSelectionOnClick
                        pageSizeOptions={[10, 25, 50, 100]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } },
                            sorting: { sortModel: [{ field: 'equConVehNom', sort: 'asc' }] },
                        }}
                        sx={{
                            borderRadius: 2,
                            backgroundColor: '#fff',
                            '& .MuiDataGrid-columnHeaders': { fontWeight: 700, backgroundColor: '#f0f0f0' },
                        }}
                    />
                </Box>
            </Stack>

            {/* Botón Flotante */}
            <Fab color="primary" aria-label="add" sx={{ position: 'fixed', bottom: 20, right: 20 }} onClick={handleOpenCreate}>
                <AddIcon />
            </Fab>

            {/* Modal de Formulario */}
            {open && (
                <TeamForm
                    open={open}
                    handleClose={handleCloseForm}
                    onSubmit={handleSubmitTeam}
                    defaultValues={editTeam || {}}
                />
            )}

            {/* Modal de Confirmación para Eliminar */}
            <Dialog open={openConfirm} onClose={handleCloseConfirm}>
                <DialogTitle>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea eliminar el equipo <strong>{selectedTeam?.equConVehNom || 'sin nombre'}</strong>?
                        Esta acción no se puede deshacer.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirm} color="inherit">
                        Cancelar
                    </Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error" disabled={deleteMutation.isPending}>
                        {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Alertas */}
            <Snackbar
                open={alert.open}
                autoHideDuration={4000}
                onClose={() => setAlert((a) => ({ ...a, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setAlert((a) => ({ ...a, open: false }))} severity={alert.severity} variant="filled" sx={{ width: '100%' }}>
                    {alert.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default TeamsDashboard;